import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { sortBy } from 'lodash'

import { ResolvedTasksRequest, ResolvedTasksResponse } from '@sparkai/shared-types'

import { apiClient } from '../apiClient'

export const RESOLVED_TASKS_QUERY_KEY = 'resolved-tasks-v3'
export const RESOLVED_TASKS_INFINITE_QUERY_KEY = `${RESOLVED_TASKS_QUERY_KEY}-infinite`

export const fetchResolvedTasks = async (
  params: ResolvedTasksRequest,
  pageParam: number
): Promise<ResolvedTasksResponse> => {
  const queryParams = { ...params }
  delete queryParams.useSelectedTimezoneContext
  const parsedParams: Omit<ResolvedTasksRequest, 'useSelectedTimezoneContext'> = {
    ...queryParams,
    cursor: pageParam,
    startTimeToClose:
      queryParams.startTimeToClose === undefined
        ? undefined
        : (Number.parseInt(queryParams.startTimeToClose, 10) * 1000).toString(),
    endTimeToClose:
      queryParams.endTimeToClose === undefined
        ? undefined
        : (Number.parseInt(queryParams.endTimeToClose, 10) * 1000).toString(),
  }
  const { data } = await apiClient.get<ResolvedTasksResponse>(`/int/v3/tasks/resolved`, {
    params: parsedParams,
    timeout: 30000,
  })
  return data
}

export function useGetResolved(params: ResolvedTasksRequest) {
  return useQuery<ResolvedTasksResponse>({
    queryKey: [RESOLVED_TASKS_QUERY_KEY, params],
    queryFn: () => fetchResolvedTasks(params, params.cursor),
    enabled:
      Boolean(params?.operatorId) &&
      Boolean(params?.startCreatedDate) &&
      Boolean(params?.endCreatedDate) &&
      Boolean(params?.limit === 10),
    refetchOnWindowFocus: false,
  })
}

export function useGetResolvedWithInfiniteQuery(params: ResolvedTasksRequest) {
  const { cursor, ...otherParams } = params

  const data = useInfiniteQuery<ResolvedTasksResponse>({
    queryKey: [RESOLVED_TASKS_INFINITE_QUERY_KEY, otherParams],
    queryFn: ({ pageParam = 0 }) => fetchResolvedTasks({ ...params, cursor }, Number(pageParam)),
    enabled:
      Boolean(params?.operatorId) &&
      Boolean(params?.startCreatedDate) &&
      Boolean(params?.endCreatedDate) &&
      Boolean(params?.limit === 16),
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: () => {
      return params.cursor + params.limit
    },
  })

  const hasPages = data?.data?.pages && data?.data?.pages.length > 0
  const total = hasPages ? data.data?.pages[0].total || 0 : 0

  const allItems = data?.data?.pages.flatMap((page) => page.resolvedTasks) ?? []

  return {
    ...data,
    total,
    items: sortBy(allItems, (item) => new Date(item.createdDate).getTime()).reverse(),
  }
}
